import React, { FC, useState } from 'react';
import { OrderStatus } from '../../models/model-enum';
import PIEText from '@src/shared/components/PIEText';
import { IconClockFilled, IconRss } from '@justeattakeaway/pie-icons-react';
import dayjs from 'dayjs';
import style from './DashboardOrderTimeVisualizer.module.scss';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import { useTranslation } from 'react-i18next';
import useRenderPhase from '@src/shared/hooks/use-render-phase';
import { useCommonPersistentStore } from '@src/shared/store/common-persistent-store';
interface IDashboardOrderTimeVisualizer {
  orderStatus: OrderStatus;
  dueTime: any;
  etaTime: any;
  compareTime: string;
  showInDeliveryDueTime: Boolean;
}
export const DashboardOrderTimeVisualizer: FC<IDashboardOrderTimeVisualizer> = ({
  orderStatus,
  dueTime,
  compareTime,
  etaTime,
  showInDeliveryDueTime = false,
}) => {
  const [etaTimeCompareValue, setEtaTimeCompareValue] = useState<string | false>(false);
  const { t } = useTranslation();
  const { persistentOrderEtaListUpdateDate, setPersistentOrderEtaListUpdateDate } =
    useCommonPersistentStore();
  const howManyMinutesLeft = (baseTime: string, serverTime: string) => {
    return dayjs(serverTime).to(dayjs(baseTime), true);
  };

  const isOrderLate = () => {
    if (!compareTime) return false;
    const dueDate = dayjs(dueTime);
    const serverDate = dayjs(compareTime as any);
    return serverDate.isAfter(dueDate);
  };

  useRenderPhase(
    () => {
      //initial render
      if (persistentOrderEtaListUpdateDate) {
        setEtaTimeCompareValue(persistentOrderEtaListUpdateDate);
      } else {
        setEtaTimeCompareValue(compareTime);
      }
    },
    () => {
      //subsequent renders
      setPersistentOrderEtaListUpdateDate(compareTime);
      setEtaTimeCompareValue(compareTime);
    },
    [etaTime]
  );

  return (
    <div className={style.DashboardOrderTimeVisualizer}>
      {orderStatus !== OrderStatus.InDelivery && (
        <PIEText
          color="var(--xds-color-content-subtle)"
          text={
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              {dayjs(dueTime as any).format('HH:mm')}
              <IconClockFilled size="xs" />
            </div>
          }
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-bold)"
          truncate={true}
        />
      )}

      {orderStatus === OrderStatus.InDelivery && !etaTime && (
        <PIEText
          color="var(--xds-color-content-subtle)"
          text={
            <>
              <PIEText
                color="var(--xds-color-content-subdued)"
                text={t('order.order_arriving_message')}
                size="var(--xds-font-size-14)"
                fontWeight="var(--xds-font-weight-regular)"
                truncate={true}
                align="right"
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                {dayjs(dueTime as any).format('HH:mm')}
                <IconClockFilled size="xs" />
              </div>
            </>
          }
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-bold)"
          truncate={true}
        />
      )}

      {orderStatus === OrderStatus.InDelivery && etaTime && compareTime && etaTimeCompareValue && (
        <>
          <PIEText
            color="var(--xds-color-content-subdued)"
            text={t('order.order_arriving_message')}
            size="var(--xds-font-size-14)"
            fontWeight="var(--xds-font-weight-regular)"
            truncate={true}
            align="right"
          />
          <PIEText
            color={'var(--xds-color-content-subtle)'}
            text={
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'end' }}
              >
                <span>{howManyMinutesLeft(etaTime || dueTime, etaTimeCompareValue)}</span>
                {t('')}
                <span className={style.LiveIconAnimationContainer}>
                  <IconRss size="xs" className={style.LiveIconAnimation} />
                </span>
              </div>
            }
            align="right"
            size="var(--xds-font-size-16)"
            fontWeight="var(--xds-font-weight-bold)"
            truncate={true}
          />
          {showInDeliveryDueTime && (
            <PIEComponentUtil marginTop="var(--xds-spacing-c)">
              <PIEText
                color="var(--xds-color-content-disabled)"
                text={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      justifyContent: 'end',
                    }}
                  >
                    {dayjs(dueTime as any).format('HH:mm')}
                    <IconClockFilled size="xs" />
                  </div>
                }
                align="right"
                size="var(--xds-font-size-16)"
                fontWeight="var(--xds-font-weight-bold)"
                truncate={true}
              />
            </PIEComponentUtil>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardOrderTimeVisualizer;
